/**
 * Lesson Runner — injects a context bar at the top of the sim container
 * when a student launches a lesson. Shows lesson name, unit badge,
 * score, progress bar, and instruction text. Wires up the lesson player
 * (sequencer + scoring engine) for the full lesson flow.
 *
 * When flowState transitions to RESULTS, shows a results overlay with
 * score breakdown, letter grade, and retry/dashboard buttons.
 */

import { LessonType, LessonFlowState } from '@graphicsim/shared-types';
import { LESSON_REGISTRY, SCORING } from '@graphicsim/shared-constants';
import { navigate } from '../../router';
import { lessonStore } from '../../stores/lessonStore';
import { loadLesson, startSequencer, stopSequencer, onInstruction, getEventResults } from '../../engine/lessonPlayer';
import { initScoringEngine, stopScoringEngine } from '../../engine/scoringEngine';
import type { AuthResult } from '../../router/routeGuard';

const UNIT_LABELS: Record<number, string> = {
  1: 'Unit 1',
  2: 'Unit 2',
  3: 'Unit 3',
  4: 'Capstone',
  5: 'Multiplayer',
};

function getBadgeClass(type: LessonType): string {
  if (type === LessonType.CAPSTONE) return 'capstone';
  if (type === LessonType.MULTIPLAYER) return 'multiplayer';
  return '';
}

function getLetterGrade(percentage: number): string {
  if (percentage >= SCORING.GRADE_THRESHOLDS.A) return 'A';
  if (percentage >= SCORING.GRADE_THRESHOLDS.B) return 'B';
  if (percentage >= SCORING.GRADE_THRESHOLDS.C) return 'C';
  if (percentage >= SCORING.GRADE_THRESHOLDS.D) return 'D';
  return 'F';
}

function getGradeColor(grade: string): string {
  switch (grade) {
    case 'A': return '#2a9f45';
    case 'B': return '#5baf5b';
    case 'C': return '#d4a017';
    case 'D': return '#d47017';
    default:  return '#c0392b';
  }
}

let unsubscribe: (() => void) | null = null;

export async function renderLessonRunner(lessonId: string, _auth: AuthResult): Promise<void> {
  const lessonMeta = LESSON_REGISTRY.find((l) => l.id === lessonId);
  if (!lessonMeta) {
    navigate('/');
    return;
  }

  // Start the lesson in the store
  lessonStore.getState().startLesson(lessonMeta.id, lessonMeta.maxScore);

  // Remove any existing overlay
  removeLessonRunner();

  const simContainer = document.getElementById('sim-container')!;
  const overlay = document.createElement('div');
  overlay.id = 'lesson-runner-overlay';

  const badgeClass = getBadgeClass(lessonMeta.type);
  const unitLabel = UNIT_LABELS[lessonMeta.unit] ?? `Unit ${lessonMeta.unit}`;

  overlay.innerHTML = `
    <button class="lr-back-btn" id="lr-back">&#x2190; Back</button>
    <div class="lr-divider"></div>
    <span class="lr-lesson-name">${lessonMeta.name}</span>
    <span class="lr-unit-badge ${badgeClass}">${unitLabel}</span>
    <div class="lr-instruction" id="lr-instruction"></div>
    <div class="lr-spacer"></div>
    <span class="lr-score" id="lr-score">0 / ${lessonMeta.maxScore}</span>
    <div class="lr-progress-bar">
      <div class="lr-progress-fill" id="lr-progress" style="width:0%"></div>
    </div>
  `;

  // Insert overlay before the first child (menubar)
  simContainer.insertBefore(overlay, simContainer.firstChild);

  // Wire back button
  const backBtn = overlay.querySelector('#lr-back');
  if (backBtn) {
    backBtn.addEventListener('click', () => {
      cleanupLesson();
      navigate('/');
    });
  }

  // Subscribe to store for live score updates + results transition
  const scoreEl = overlay.querySelector('#lr-score') as HTMLElement | null;
  const progressEl = overlay.querySelector('#lr-progress') as HTMLElement | null;
  const instructionEl = overlay.querySelector('#lr-instruction') as HTMLElement | null;

  unsubscribe = lessonStore.subscribe((state) => {
    if (scoreEl) {
      scoreEl.textContent = `${state.score} / ${state.maxScore}`;
    }
    if (progressEl && state.maxScore > 0) {
      const pct = Math.min(100, (state.score / state.maxScore) * 100);
      progressEl.style.width = `${pct}%`;
    }
    // Transition to results screen
    if (state.flowState === LessonFlowState.RESULTS) {
      showResultsOverlay(lessonMeta.id, state.score, state.maxScore);
    }
  });

  // Wire instruction callback
  onInstruction((text: string) => {
    if (instructionEl) {
      instructionEl.textContent = text;
      instructionEl.classList.add('lr-instruction-active');
    }
  });

  // Load lesson JSON and start sequencer + scoring engine
  try {
    const lessonData = await loadLesson(lessonId);
    if (lessonData.timestamps && lessonData.timestamps.length > 0) {
      initScoringEngine();
      startSequencer(lessonData);
    }
  } catch (err) {
    console.warn(`Could not load lesson data for ${lessonId}:`, err);
    // Lesson still runs — just no sequencer (free explore mode)
  }
}

function showResultsOverlay(lessonId: string, score: number, maxScore: number): void {
  // Remove any existing results overlay
  const existing = document.getElementById('lesson-results-overlay');
  if (existing) existing.remove();

  const percentage = maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
  const letterGrade = getLetterGrade(percentage);
  const gradeColor = getGradeColor(letterGrade);
  const results = getEventResults();

  // Build event results breakdown
  let breakdownHtml = '';
  if (results.length > 0) {
    breakdownHtml = `<div class="results-breakdown">
      <div class="results-breakdown-header">Event Results</div>
      ${results.map((r) => `
        <div class="results-event-row ${r.pointsEarned > 0 ? 'correct' : 'missed'}">
          <span class="results-event-type">${r.type}</span>
          <span class="results-event-expected">${r.expected}</span>
          <span class="results-event-actual">${r.actual}</span>
          <span class="results-event-points">${r.pointsEarned} / ${r.pointsPossible}</span>
        </div>
      `).join('')}
    </div>`;
  }

  const overlay = document.createElement('div');
  overlay.id = 'lesson-results-overlay';
  overlay.innerHTML = `
    <div class="results-card">
      <div class="results-title">Lesson Complete</div>
      <div class="results-grade" style="color:${gradeColor}">${letterGrade}</div>
      <div class="results-score">${score} / ${maxScore}</div>
      <div class="results-percentage">${percentage}%</div>
      ${breakdownHtml}
      <div class="results-actions">
        <button class="results-btn results-retry" id="results-retry">Retry Lesson</button>
        <button class="results-btn results-dashboard" id="results-dashboard">Back to Dashboard</button>
      </div>
    </div>
  `;

  document.getElementById('sim-container')!.appendChild(overlay);

  // Wire buttons
  overlay.querySelector('#results-retry')?.addEventListener('click', () => {
    cleanupLesson();
    // Re-navigate to same lesson
    navigate(`/lesson/${lessonId}`);
  });

  overlay.querySelector('#results-dashboard')?.addEventListener('click', () => {
    cleanupLesson();
    navigate('/');
  });
}

function cleanupLesson(): void {
  stopSequencer();
  stopScoringEngine();
  lessonStore.getState().reset();
  removeLessonRunner();
}

export function removeLessonRunner(): void {
  const existing = document.getElementById('lesson-runner-overlay');
  if (existing) existing.remove();

  const results = document.getElementById('lesson-results-overlay');
  if (results) results.remove();

  if (unsubscribe) {
    unsubscribe();
    unsubscribe = null;
  }
}
